import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import "@/App.css";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { api } from "@/lib/api";
import { hasAccess } from "@/lib/roles";

import Layout from "@/components/Layout";
import Home from "@/pages/Home";
import Catalog from "@/pages/Catalog";
import EbookDetail from "@/pages/EbookDetail";
import Cart from "@/pages/Cart";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Library from "@/pages/Library";
import Reader from "@/pages/Reader";
import ScrollToTop from "@/components/ScrollToTop";
import Affiliates from "@/pages/Affiliates";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import GuestAccess from "@/pages/GuestAccess";
import ComingSoon from "@/pages/ComingSoon";
import ChangePassword from "@/pages/ChangePassword";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";

import AdminLayout from "@/pages/admin/AdminLayout";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminEbooks from "@/pages/admin/AdminEbooks";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminSubscribers from "@/pages/admin/AdminSubscribers";
import AdminAffiliates from "@/pages/admin/AdminAffiliates";
import AdminCoupons from "@/pages/admin/AdminCoupons";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminEmails from "@/pages/admin/AdminEmails";

import AgentLayout from "@/pages/agent/AgentLayout";
import AgentLogin from "@/pages/agent/AgentLogin";
import AgentDashboard from "@/pages/agent/AgentDashboard";
import AgentLeads from "@/pages/agent/AgentLeads";
import AgentTaxRecords from "@/pages/agent/AgentTaxRecords";
import AgentFlags from "@/pages/agent/AgentFlags";

function PublicRoutes({ comingSoon }) {
  const location = useLocation();
  const { user } = useAuth();
  const isAdminRoute = location.pathname.startsWith("/admin");
  const isAgentRoute = location.pathname.startsWith("/agent");
  const isPortalRoute = isAdminRoute || isAgentRoute;

  // Force change-password flow when a logged-in user has must_change_password=true
  if (user?.must_change_password && location.pathname !== "/change-password") {
    return (
      <Routes>
        <Route path="*" element={<ChangePassword forced={true} />} />
      </Routes>
    );
  }

  // If comingSoon is ON and not on an internal portal route, force Coming Soon
  if (comingSoon && !isPortalRoute) {
    return (
      <Routes>
        <Route path="/admin/*" element={null} />
        <Route path="/agent/*" element={null} />
        <Route path="*" element={<ComingSoon />} />
      </Routes>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Routes>
      <Route path="/" element={<Layout><Home /></Layout>} />
      <Route path="/catalog" element={<Layout><Catalog /></Layout>} />
      <Route path="/ebook/:slug" element={<Layout><EbookDetail /></Layout>} />
      <Route path="/cart" element={<Layout><Cart /></Layout>} />
      <Route path="/login" element={<Layout><Login /></Layout>} />
      <Route path="/register" element={<Layout><Register /></Layout>} />
      <Route path="/library" element={<Layout><Library /></Layout>} />
      <Route path="/read/:slug" element={<Reader />} />
      <Route path="/affiliates" element={<Layout><Affiliates /></Layout>} />
      <Route path="/change-password" element={<ChangePassword />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/payment/success" element={<Layout><PaymentSuccess /></Layout>} />
      <Route path="/payment/cancel" element={<Layout><PaymentCancel /></Layout>} />
      <Route path="/access/:token" element={<Layout><GuestAccess /></Layout>} />
      <Route path="/coming-soon" element={<ComingSoon />} />
      {/* Admin */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<AdminLayout />}>
        <Route index element={<AdminDashboard />} />
        <Route path="ebooks" element={<AdminEbooks />} />
        <Route path="orders" element={<AdminOrders />} />
        <Route path="subscribers" element={<AdminSubscribers />} />
        <Route path="emails" element={<AdminEmails />} />
        <Route path="affiliates" element={<AdminAffiliates />} />
        <Route path="coupons" element={<AdminCoupons />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="settings" element={<AdminSettings />} />
      </Route>
      {/* Agent Portal — FloridaTaxProperty.com */}
      <Route path="/agent/login" element={<AgentLogin />} />
      <Route path="/agent" element={<AgentLayout />}>
        <Route index element={<AgentDashboard />} />
        <Route path="leads" element={<AgentLeads />} />
        <Route path="tax-records" element={<AgentTaxRecords />} />
        <Route path="flags" element={<AgentFlags />} />
      </Route>
      <Route path="*" element={<Layout><Home /></Layout>} />
    </Routes>
    </>
  );
}

function AppShell() {
  const [comingSoon, setComingSoon] = useState(false);
  const [ready, setReady] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    api.get("/settings").then((r) => setComingSoon(!!r.data.coming_soon_mode)).catch(() => {}).finally(() => setReady(true));
  }, []);

  // Admins always see full site (any admin-tier role)
  const effectiveComingSoon = comingSoon && !hasAccess(user?.role, "admin");
  if (!ready) return null;
  return <PublicRoutes comingSoon={effectiveComingSoon} />;
}

function App() {
  return (
    <CartProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppShell />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </CartProvider>
  );
}

export default App;
